import React from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { Badge } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { CrudPage } from "../components/CrudPage";
import { RecordDetailsModal } from "../components/RecordDetailsModal";
import { useAuth } from "../context/AuthContext";
import { easyPurchase, requests } from "../services/api";
import { TableFilterDefinition } from "../types";

const formatRial = (value?: number) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const statusMeta: Record<string, { label: string; color: string }> = {
  wait_for_payment: { label: "در انتظار پرداخت", color: "orange" },
  reviewing: { label: "در حال بررسی", color: "blue" },
  approved: { label: "تایید شده", color: "green" },
  rejected: { label: "رد شده", color: "red" },
  failed: { label: "ناموفق", color: "red" },
};

const paymentMethodMeta: Record<string, string> = {
  gateway: "درگاه پرداخت",
  card: "کارت هدیه",
  slip: "فیش انتقال",
};

const paymentClientMeta: Record<string, string> = {
  mobile: "اپلیکیشن موبایل",
  web: "وب",
};

const operationTypeMeta: Record<string, string> = {
  issuance: "صدور",
  redemption: "ابطال",
};

const externalSourceMeta: Record<string, string> = {
  sepah_broker: "کارگزاری سپه",
  tadbir: "تدبیر",
};

const columns = [
  {
    header: "سرمایه‌گذار",
    accessor: "investor",
    render: (item: any) => formatInvestorLabel(item.investor),
  },
  {
    header: "صندوق",
    accessor: "fundName",
    isSortable: true,
    render: (item: any) => item.fundName || item.fundSeoRegisterNo || "—",
  },
  {
    header: "نوع عملیات",
    accessor: "operationType",
    isSortable: true,
    render: (item: any) => operationTypeMeta[item.operationType] || "—",
  },
  {
    header: "مبلغ",
    accessor: "amountRial",
    isSortable: true,
    render: (item: any) => formatRial(item.amountRial),
  },
  {
    header: "واحد",
    accessor: "units",
    isSortable: true,
    render: (item: any) => item.units,
  },
  {
    header: "وضعیت",
    accessor: "status",
    isSortable: true,
    render: (item: any) => {
      const meta = statusMeta[item.status] || {
        label: item.status || "—",
        color: "gray",
      };
      return <Badge colorScheme={meta.color}>{meta.label}</Badge>;
    },
  },
  {
    header: "روش پرداخت",
    accessor: "paymentMethod",
    isSortable: true,
    render: (item: any) => paymentMethodMeta[item.paymentMethod] || "—",
  },
  {
    header: "کلاینت پرداخت",
    accessor: "paymentClient",
    isSortable: true,
    render: (item: any) =>
      paymentClientMeta[String(item.paymentClient || "").trim()] ||
      item.paymentClient ||
      "—",
  },
  {
    header: "منبع",
    accessor: "externalSource",
    isSortable: true,
    render: (item: any) =>
      externalSourceMeta[String(item.externalSource || "").trim()] ||
      item.externalSource ||
      "داخلی",
  },
  {
    header: "تاریخ",
    accessor: "createdAt",
    isSortable: true,
    render: (item: any) => formatDate(item.createdAt),
  },
];

const requestFilterDefinitions: TableFilterDefinition[] = [
  {
    key: "status",
    label: "وضعیت",
    type: "select",
    options: Object.entries(statusMeta).map(([value, meta]) => ({
      value,
      label: meta.label,
    })),
  },
  {
    key: "operationType",
    label: "نوع عملیات",
    type: "select",
    options: Object.entries(operationTypeMeta).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    key: "paymentMethod",
    label: "روش پرداخت",
    type: "select",
    options: Object.entries(paymentMethodMeta).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    key: "externalSource",
    label: "منبع",
    type: "select",
    options: [
      ...Object.entries(externalSourceMeta).map(([value, label]) => ({
        value,
        label,
      })),
      { value: "internal", label: "داخلی" },
    ],
  },
  {
    key: "amountMin",
    label: "حداقل مبلغ (ریال)",
    type: "number",
    placeholder: "از مبلغ",
    useThousandsSeparator: true,
  },
  {
    key: "amountMax",
    label: "حداکثر مبلغ (ریال)",
    type: "number",
    placeholder: "تا مبلغ",
    useThousandsSeparator: true,
  },
  {
    key: "createdFrom",
    label: "تاریخ ثبت از",
    type: "date",
    placeholder: "تاریخ شروع",
    usePersianDatePicker: true,
  },
  {
    key: "createdTo",
    label: "تاریخ ثبت تا",
    type: "date",
    placeholder: "تاریخ پایان",
    usePersianDatePicker: true,
  },
];

export const RequestsPage: React.FC = () => {
  const detailsModal = useDisclosure();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const canViewRequestHistory = hasPermission("requestHistories:view");

  const handleView = async (item: any) => {
    try {
      const fullItem = await requests.getOne(item._id);
      setSelectedItem(fullItem);
      detailsModal.onOpen();
    } catch (error: any) {
      toast({
        title: "خطا در دریافت جزئیات درخواست",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const refreshSelectedItem = async (requestId: string) => {
    const fullItem = await requests.getOne(requestId);
    setSelectedItem(fullItem);
  };

  const handleReconcileTadbir = async (requestId: string) => {
    const response = await requests.reconcileTadbir(requestId);
    setSelectedItem(response?.data || null);
    await queryClient.invalidateQueries(["requests"]);
    if (!response?.data) {
      await refreshSelectedItem(requestId);
    }
    toast({
      title: "همگام‌سازی انجام شد",
      description: response?.message || "درخواست با تدبیر همگام شد.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleRetryTadbirIssuance = async (requestId: string) => {
    const response = await requests.retryTadbirIssuance(requestId);
    setSelectedItem(response?.data || null);
    await queryClient.invalidateQueries(["requests"]);
    if (!response?.data) {
      await refreshSelectedItem(requestId);
    }
    toast({
      title: "ارسال مجدد انجام شد",
      description:
        response?.message || "تلاش مجدد برای ارسال صدور به تدبیر انجام شد.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCheckSepahPayment = async (requestId: string) => {
    const response = await easyPurchase.confirmSepahPayment(requestId);
    setSelectedItem(response?.data || null);
    await queryClient.invalidateQueries(["requests"]);
    if (!response?.data) {
      await refreshSelectedItem(requestId);
    }
    toast({
      title: response?.succeeded
        ? "بررسی پرداخت سپه انجام شد"
        : "بررسی پرداخت سپه انجام شد",
      description:
        response?.message || "وضعیت پرداخت سپه برای این درخواست بررسی شد.",
      status: response?.succeeded ? "success" : "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSubmitSepahBuyOrder = async (requestId: string) => {
    const response = await easyPurchase.submitSepahBuyOrder(requestId);
    setSelectedItem(response?.data || null);
    await queryClient.invalidateQueries(["requests"]);
    if (!response?.data) {
      await refreshSelectedItem(requestId);
    }
    toast({
      title: response?.succeeded
        ? "سفارش خرید سپه ثبت شد"
        : "ثبت سفارش خرید سپه ناموفق بود",
      description:
        response?.message || "عملیات ثبت سفارش خرید سپه انجام شد.",
      status: response?.succeeded ? "success" : "error",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <>
      <CrudPage
        title="درخواست‌های امید"
        columns={columns}
        queryKey="requests"
        resourceName="requests"
        api={requests}
        showAddButton={false}
        enableEdit={false}
        enableDelete={false}
        enableView
        customViewAction={handleView}
        FormComponent={() => null}
        filterDefinitions={requestFilterDefinitions}
        initialSort={{ field: "createdAt", direction: "desc" }}
      />

      <RecordDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={detailsModal.onClose}
        title={getRequestDetailsTitle(selectedItem)}
        data={buildRequestDetailsPayload(selectedItem)}
        paymentMethod={selectedItem?.paymentMethod}
        loadHistory={
          canViewRequestHistory
            ? (requestId, page, pageSize) =>
                requests.getHistory(requestId, {
                  page,
                  pageSize,
                  sort: "asc",
                })
            : undefined
        }
        onReconcileTadbirRequest={
          canViewRequestHistory ? handleReconcileTadbir : undefined
        }
        onRetryTadbirIssuanceRequest={
          canViewRequestHistory ? handleRetryTadbirIssuance : undefined
        }
        onCheckSepahPayment={handleCheckSepahPayment}
        onSubmitSepahBuyOrder={handleSubmitSepahBuyOrder}
      />
    </>
  );
};

const formatInvestorLabel = (investor?: any) => {
  const fullName = String(investor?.fullName || "").trim();
  const nationalId = String(investor?.nationalId || "").trim();

  if (fullName && nationalId) {
    return `${fullName} - ${nationalId}`;
  }

  return fullName || nationalId || "—";
};

const buildRequestDetailsPayload = (item: any) => {
  if (!item) {
    return null;
  }

  const commonDetails = {
    _id: item._id,
    trackId: item.trackId,
    fundName: item.fundName,
    fundSeoRegisterNo: item.fundSeoRegisterNo,
    operationType: item.operationType,
    paymentMethod: item.paymentMethod,
    amountRial: item.amountRial,
    units: item.units,
    unitPrice: item.unitPrice,
    status: item.status,
    externalSource: item.externalSource,
    externalId: item.externalId,
    externalStatus: item.externalStatus,
    externalLastSyncedAt: item.externalLastSyncedAt,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    investor: item.investor,
    history: Array.isArray(item.history) ? item.history : [],
    historyCount: Number(item.historyCount || 0),
  };

  if (item.paymentMethod === "card") {
    return {
      ...commonDetails,
      paymentCard: item.paymentCard || null,
      transaction: item.transaction || null,
    };
  }

  if (item.paymentMethod === "slip") {
    return {
      ...commonDetails,
      paymentSlip: item.paymentSlip || null,
      transaction: item.transaction || null,
    };
  }

  return {
    ...commonDetails,
    transaction: item.transaction || null,
  };
};

const getRequestDetailsTitle = (item: any) => {
  const paymentMethodLabel = paymentMethodMeta[item?.paymentMethod || ""];

  if (paymentMethodLabel) {
    return `جزئیات درخواست ${paymentMethodLabel}`;
  }

  return "جزئیات درخواست";
};
